import { Flex, Spin } from 'antd'
import dayjs from 'dayjs'
import { concat, isNil, toNumber } from 'lodash-es'
import compact from 'lodash-es/compact'
import head from 'lodash-es/head'
import isUndefined from 'lodash-es/isUndefined'
import { FunctionComponent, useCallback, useMemo, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { ApexChartOptions, ApexCharts } from '@/components/common/apex-charts'
import ApexChartsYAxisLabels from '@/components/common/apexcharts/apex-charts-y-axis-labels'
import Blur from '@/components/common/blur'
import { Duration } from '@/components/common/duration-select'
import KolDetailDataBlock from '@/components/kol/common/kol-detail-data-block'
import KolDetailDataBlockSubHeader from '@/components/kol/common/kol-detail-data-block-sub-header'
import KolDetailStatistic from '@/components/kol/common/kol-detail-statistic'
import TimeGranularitySelector, {
  Granularity,
} from '@/components/kol/common/time-granularity-selector'
import { StatisticWrapper } from '@/components/kol/detail/audience-analyses/audience-analysis'
import Blocker from '@/components/kol/detail/blocker'
import { initialPostFilterConditions } from '@/components/kol/detail/creative-content-analyses/post-list'
import { GRANULARITY_CHART_WIDTH_MAP } from '@/components/kol/detail/engagement-analyses/common/schema'
import {
  ChartContent,
  LegendColor,
  LegendLabel,
  LegendWrapper,
} from '@/components/kol/detail/engagement-analyses/common/styled'
import { NoData } from '@/components/kol/detail/no-data'
import useKolAnalyze from '@/components/kol/hooks/use-kol-analyze'
import useKolDetailRoute from '@/components/kol/hooks/use-kol-detail-route'
import { mockFixedPostFrequencyData } from '@/constants/kol-detail/mock-data'
import { PostType } from '@/constants/post-type'
import {
  DURATION_MONTH_INTEGER_MAP,
  KolDetailDuration,
} from '@/hooks/use-authorization/routes'
import { useI18n } from '@/hooks/use-i18n'
import { useIntl } from '@/i18n/hooks/use-intl'
import { FormatDateType } from '@/i18n/typing'
import type { Optional } from '@/types/optional'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import { Platform, PlatformShortcode } from '@/utils/convert-platform'
import formatDuration from '@/utils/detail/format-duration'
import {
  GroupByPostType,
  groupPostByGranularity,
} from '@/utils/detail/group-by-granularity'
import inTimeBoundDate from '@/utils/detail/in-time-bound-date'
import { getBoundaryDateSet } from '@/utils/format-date'
import getStartEndIsoStrings from '@/utils/get-start-end-iso-strings'
import mergeZeroData from '@/utils/kol/merge-zero-data'
import { scrollToContent } from '@/utils/scroll-to-content'

interface PostFrequencyAnalysisProps {
  kolInfo?: KolSchemaModel
  platform: Platform
  duration: KolDetailDuration
  isKolInfoLocked: boolean
  isPublicPage: boolean
  showMockData?: boolean
  isFetchingKolInfo: boolean
}

interface PostFrequencyData {
  name: string
  data: { x: string; y: number }[]
  color: string
  postType: PostType.Video | PostType.Image | PostType.Live
}

const PostFrequencyAnalysis: FunctionComponent<PostFrequencyAnalysisProps> = ({
  kolInfo,
  platform,
  isKolInfoLocked,
  duration,
  isPublicPage,
  showMockData,
  isFetchingKolInfo,
}) => {
  const { formatMessage, formatDate, formatNumber } = useIntl()
  const theme = useTheme()
  const { locale } = useI18n()
  const { searchParams, setSearchParams } = useKolDetailRoute()

  const [timeGranularity, setTimeGranularity] = useState<Granularity>('week')

  const { analyze = [], isFetching } = useKolAnalyze({
    kolId: kolInfo?.id,
    kolDcIdHashed: kolInfo?.dcIdHashed,
    isKolInfoLocked,
    platform: platform.code,
  })

  const kpi = kolInfo?.kpi.find((kpi) => kpi.platform === platform.shortcode)
  const timeRange = duration === Duration.ThreeMonths ? 3 : 6
  const postCount = ((): number | null => {
    if (!kpi) {
      return null
    }

    const count =
      duration === Duration.ThreeMonths ? kpi.postCount3m : kpi.postCount6m
    return count === 0 ? null : count
  })()

  const postFrequencyValue = ((): Optional<number> => {
    const value =
      duration === Duration.ThreeMonths
        ? kpi?.postFrequency3m
        : kpi?.postFrequency6m

    return value ?? undefined
  })()
  const analyzes = isKolInfoLocked ? mockFixedPostFrequencyData : analyze

  const durationBeginDate = dayjs().subtract(
    DURATION_MONTH_INTEGER_MAP[duration],
    'months',
  )

  const inTimeData = useMemo(
    () => inTimeBoundDate(analyzes, timeRange, 'publishedAt'),
    [analyzes, timeRange],
  )
  const mergedMockPostData = useMemo(
    () => mergeZeroData(inTimeData, timeRange),
    [timeRange, inTimeData],
  )
  const mergedMockVideoData = useMemo(
    () =>
      mergeZeroData(mergedMockPostData, timeRange, {
        isVideo: true,
      }),
    [timeRange, mergedMockPostData],
  )

  const fullTimeData = useMemo(
    () =>
      mergeZeroData(mergedMockVideoData, timeRange, {
        isLive: true,
        isVideo: true,
      }),
    [timeRange, mergedMockVideoData],
  )

  const getData = useCallback(
    (postType: GroupByPostType) =>
      groupPostByGranularity(fullTimeData, timeGranularity, postType).map(
        (d) => {
          return { y: d.amount, x: d.publishedAt }
        },
      ),
    [fullTimeData, timeGranularity],
  )

  const postData = useMemo<PostFrequencyData>(
    () => ({
      name: formatMessage({ id: 'kol:post_lable_text_image' }),
      data: getData('post'),
      color: theme.colors.chart.cyan1,
      postType: PostType.Image,
    }),
    [formatMessage, getData],
  )

  const videoData = useMemo<PostFrequencyData>(
    () => ({
      name: formatMessage({ id: 'kol:post_lable_video' }),
      data: getData('video'),
      color: theme.colors.chart.blue3,
      postType: PostType.Video,
    }),
    [formatMessage, getData],
  )

  const liveData = useMemo<PostFrequencyData>(
    () => ({
      name: formatMessage({ id: 'kol:graph_illu_live' }),
      data: getData('live'),
      color: theme.colors.chart.purple2,
      postType: PostType.Live,
    }),
    [formatMessage, getData],
  )

  const barSeries = compact(
    concat([
      videoData.data.every((_data) => _data.y === 0) ? null : videoData,
      liveData.data.every((_data) => _data.y === 0) ? null : liveData,
      postData.data.every((_data) => _data.y === 0) ? null : postData,
    ]),
  )

  const noData =
    barSeries.every((_barSeries) =>
      _barSeries?.data.every((data) => data.y === 0),
    ) || isFetching

  const boundaryDates = getBoundaryDateSet(
    head(barSeries)?.data.map((data) => data.x) ?? [],
  )
  const options: ApexChartOptions<PostFrequencyData[]> = {
    chart: {
      id: 'post-frequency-chart',
      stacked: true,
      height: 300,
      toolbar: {
        show: false,
      },
      events: {
        click: async (_event, _chartContext, options): Promise<void> => {
          if (
            !options ||
            isUndefined(options.dataPointIndex) ||
            isUndefined(options.seriesIndex) ||
            options.dataPointIndex < 0 ||
            options.seriesIndex < 0 ||
            !options.config?.series?.length
          ) {
            return
          }

          const { seriesIndex, dataPointIndex } = options
          if (seriesIndex !== -1 && dataPointIndex !== -1) {
            const between = formatDuration(
              dayjs(options.config.series[seriesIndex].data[dataPointIndex].x),
              timeGranularity,
              true,
              dataPointIndex === 0 ? durationBeginDate : undefined,
            )

            scrollToContent()

            const [postStartDate, postEndDate] = between.split(' ~ ')
            const [postStartTime, postEndTime] = getStartEndIsoStrings([
              postStartDate,
              postEndDate,
            ])
            const postListPostTypes = ((): PostType[] | undefined => {
              const postType = barSeries[seriesIndex]?.postType
              if (!postType) {
                return []
              }
              if (postType === PostType.Video) {
                return platform.shortcode === PlatformShortcode.Facebook
                  ? [PostType.Video]
                  : [PostType.Video, PostType.Short]
              }
              return [postType] // PostType.Image, PostType.Live
            })()

            await setSearchParams(
              {
                ...searchParams.data,
                ...initialPostFilterConditions,
                postListPostTypes,
                postListPlatform: platform.shortcode,
                postListDateRangeStart: postStartTime,
                postListDateRangeEnd: postEndTime,
              },
              'postList',
            )
          }
        },
      },
    },
    xaxis: {
      labels: {
        rotateAlways: boundaryDates.size > 1,
        formatter: (value, _timestamp, opts): string => {
          const formatType = ((): FormatDateType => {
            if (timeGranularity === 'month') {
              return boundaryDates.has(value)
                ? 'localeYearMonth'
                : 'localeMonth'
            }
            return boundaryDates.has(value) ? 'default' : 'numericMonthDay'
          })()

          if (timeGranularity === 'week') {
            return formatDate(
              opts?.i === 0
                ? durationBeginDate.toISOString()
                : dayjs(value).startOf('week').toISOString(),
              formatType,
            )
          }

          return formatDate(value, formatType)
        },
      },
    },
    yaxis: {
      labels: {
        show: false,
        formatter: (value): string => {
          return toNumber(value).toFixed(0)
        },
      },
    },
    legend: {
      show: false,
    },
    tooltip: {
      x: {
        formatter: (value, opt): string => {
          const { seriesIndex, dataPointIndex, w } = opt
          const time =
            value || w.config.series[seriesIndex].data[dataPointIndex].x

          return `<div><div>${formatDuration(
            dayjs(time),
            timeGranularity,
            false,
            dataPointIndex === 0 ? durationBeginDate : undefined,
          )}</div>
            <div style="color:${theme.colors.unclassified.grey.grey1}; text-align: center;margin-top: 4px">${formatMessage(
              {
                id: 'irm:kol_detail_chart_click_search_content',
              },
            )}</div>
            </div>`
        },
      },
    },
    grid: {
      show: true,
      xaxis: {
        lines: {
          show: true,
        },
      },
    },
  }

  const handleSelectTimeGranularity = (selectedValue: Granularity): void => {
    setTimeGranularity(selectedValue)
  }

  return (
    <KolDetailDataBlock
      colSpan={12}
      header={{
        title: formatMessage(
          { id: 'kol:post_frequency_title' },
          { platform: platform.title },
        ),
      }}
      kolId={kolInfo?.id}
      kolMetrics='Post Frequency'
      kolName={kolInfo?.name}
      platform={platform.shortcode}
    >
      <Flex vertical gap={30} style={{ height: '100%' }}>
        <StatisticWrapper>
          <KolDetailStatistic
            loading={isFetchingKolInfo}
            title={formatMessage({ id: 'kol:post_card_content' })}
            tooltip={{
              title: formatMessage({ id: 'kol:kolcard_content_tooltip' }),
            }}
            value={postCount}
          />
          <KolDetailStatistic
            loading={isFetchingKolInfo}
            suffix={
              isUndefined(postFrequencyValue) ? undefined : (
                <Frequency>
                  {formatMessage({ id: 'general:unit_month' })}
                </Frequency>
              )
            }
            title={formatMessage({ id: 'kol:post_frequency_chart' })}
            value={
              isNil(postFrequencyValue) || postFrequencyValue === 0
                ? null
                : formatMessage(
                    { id: 'kol:chart_post_unit' },
                    {
                      n: formatNumber(postFrequencyValue),
                    },
                  )
            }
          />
        </StatisticWrapper>
        <KolDetailDataBlockSubHeader
          extraContent={
            <TimeGranularitySelector
              disabled={noData}
              value={noData ? undefined : timeGranularity}
              onChange={handleSelectTimeGranularity}
            />
          }
          title={formatMessage(
            { id: 'kol:post_frequency_subtitle' },
            {
              platform: '',
            },
          )}
          tooltip={{
            title: formatMessage({ id: 'kol:post_time_analysis_subtitle' }),
          }}
        />
        {isFetching || isFetchingKolInfo ? (
          <Flex align='center' justify='center' style={{ minHeight: 200 }}>
            <Spin />
          </Flex>
        ) : (
          <Spin indicator={<Blocker />} spinning={isPublicPage}>
            <Blur activate={isKolInfoLocked && !showMockData}>
              {noData ? (
                <NoData />
              ) : (
                <Flex vertical gap={10}>
                  <Flex style={{ overflow: 'hidden' }}>
                    <ApexChartsYAxisLabels
                      key={locale}
                      options={options}
                      series={barSeries}
                      type='bar'
                    />
                    <div style={{ overflow: 'auto hidden', flex: 1 }}>
                      <ChartContent
                        width={GRANULARITY_CHART_WIDTH_MAP[timeGranularity]}
                      >
                        <ApexCharts
                          key={locale}
                          options={options}
                          series={barSeries}
                          type='bar'
                        />
                      </ChartContent>
                    </div>
                  </Flex>
                  <LegendWrapper>
                    {barSeries.map((s) => (
                      <Flex gap={6} key={s.name}>
                        <LegendColor color={s.color} />
                        <LegendLabel> {s.name} </LegendLabel>
                      </Flex>
                    ))}
                  </LegendWrapper>
                </Flex>
              )}
            </Blur>
          </Spin>
        )}
      </Flex>
    </KolDetailDataBlock>
  )
}

export default PostFrequencyAnalysis

const Frequency = styled.span`
  color: #2f2f2f;
  font-size: 14px;
  align-self: flex-end;

  ::before {
    content: '/ ';
  }
`
